import * as React from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import DailyQuestAdminInstanceDto from '../models/dtos/DailyQuestAdminInstanceDto';
import { DailyQuestStatus } from '../models/dtos/DailyQuestTemplateDto';

/** Status filter options, in lifecycle order. */
export const DAILY_QUEST_STATUS_OPTIONS: DailyQuestStatus[] = ['OFFERED', 'PENDING', 'COMPLETED', 'EXPIRED', 'CANCELLED', 'DECLINED'];

const STATUS_VIEW: Record<DailyQuestStatus, { color: ChipProps['color']; variant: ChipProps['variant']; hint: string }> = {
    OFFERED: { color: 'info', variant: 'filled', hint: 'Offered by Ace, waiting for the player to accept or decline' },
    PENDING: { color: 'warning', variant: 'filled', hint: 'Active quest' },
    COMPLETED: { color: 'success', variant: 'filled', hint: 'All tasks done' },
    EXPIRED: { color: 'default', variant: 'outlined', hint: 'Ran out of time' },
    CANCELLED: { color: 'error', variant: 'outlined', hint: 'Cancelled' },
    DECLINED: { color: 'default', variant: 'filled', hint: 'The player declined Ace\'s offer' },
};

/** Ace's own colour (the theme's secondary is grey, too close to DECLINED). */
export const ACE_CHIP_SX = { bgcolor: deepPurple[500], color: '#fff', fontWeight: 600 } as const;

/** DECLINED without an answer time = the offer lapsed unanswered. */
export function isIgnoredOffer(quest: Pick<DailyQuestAdminInstanceDto, 'status' | 'answeredAt'>): boolean {
    return quest.status === 'DECLINED' && quest.answeredAt == null;
}

export function DailyQuestStatusChip({ quest, prefix }: {
    quest: Pick<DailyQuestAdminInstanceDto, 'status' | 'answeredAt'>;
    prefix?: string;
}) {
    const view = STATUS_VIEW[quest.status] ?? { color: 'default', variant: 'outlined', hint: quest.status };
    const ignored = isIgnoredOffer(quest);
    const label = ignored ? 'Ignored' : quest.status;
    return (
        <Tooltip title={ignored ? 'Ace\'s offer lapsed without an answer' : view.hint}>
            <Chip size="small" color={view.color} variant={view.variant} label={prefix ? `${prefix}${label}` : label} />
        </Tooltip>
    );
}

export function DailyQuestSourceChip({ quest }: { quest: Pick<DailyQuestAdminInstanceDto, 'source'> }) {
    return quest.source === 'ACE'
        ? (
            <Tooltip title="Offered by Ace on iOS">
                <Chip size="small" label="Ace" sx={ACE_CHIP_SX} />
            </Tooltip>
        )
        : (
            <Tooltip title="Assigned automatically">
                <Chip size="small" variant="outlined" label="AUTO" />
            </Tooltip>
        );
}
